/**
 * EmojiPickerMenu.tsx
 *
 * The emoji popover shared by the comment reaction picker (CommentList) and the
 * composer's emoji inserter (CommentComposer) — a horizontal row of
 * `QUICK_REACTIONS`. It owns the visuals, the menu/menuitem roles and the
 * per-emoji buttons; the caller owns the open state, the portal and the
 * positioning (pass `useAnchoredDropdown`'s `style` and `menuRef` straight
 * through).
 *
 *   {open && style &&
 *     createPortal(
 *       <EmojiPickerMenu
 *         ref={menuRef}
 *         style={style}
 *         testId="comment-reaction-picker"
 *         optionTestId="comment-reaction-option"
 *         labelFor={emoji => `React ${emoji}`}
 *         onPick={emoji => …}
 *       />,
 *       document.body,
 *     )}
 */

import React from 'react';
import styled from 'styled-components';

import { QUICK_REACTIONS } from './quickReactions';

// Panel geometry lives here, in one place, because the width is needed twice:
// the styled components below consume it, and callers must hand it to
// `useAnchoredDropdown`, which sets an explicit inline width (defaulting to the
// trigger's, far too narrow here). Deriving the total from these numbers keeps
// the padding even on both sides — a hardcoded width left dead space on the
// right, and would drift again the moment QUICK_REACTIONS changed length.
const OPTION_SIZE = 30;
const GAP = 2;
const PADDING = 4;
const BORDER = 1;

/** Content width of the picker, including padding and borders (border-box). */
export const EMOJI_PICKER_MENU_WIDTH =
  QUICK_REACTIONS.length * OPTION_SIZE +
  (QUICK_REACTIONS.length - 1) * GAP +
  PADDING * 2 +
  BORDER * 2;

interface EmojiPickerMenuProps {
  /** Positioning from `useAnchoredDropdown`; visuals live in `Menu` below. */
  style: React.CSSProperties;
  /** Test id for the menu itself. */
  testId: string;
  /** Test id for each emoji button, suffixed `-${emoji}`. */
  optionTestId: string;
  /** Accessible label per emoji — e.g. `React 👍` vs `Insert 👍`. */
  labelFor: (emoji: string) => string;
  onPick: (emoji: string) => void;
}

export const EmojiPickerMenu = React.forwardRef<
  HTMLDivElement,
  EmojiPickerMenuProps
>(({ style, testId, optionTestId, labelFor, onPick }, ref) => (
  <Menu ref={ref} style={style} role="menu" data-testid={testId}>
    {QUICK_REACTIONS.map(emoji => (
      <Option
        key={emoji}
        type="button"
        role="menuitem"
        aria-label={labelFor(emoji)}
        data-testid={`${optionTestId}-${emoji}`}
        onClick={() => onPick(emoji)}
      >
        {emoji}
      </Option>
    ))}
  </Menu>
));

EmojiPickerMenu.displayName = 'EmojiPickerMenu';

const Menu = styled.div`
  display: flex;
  align-items: center;
  gap: ${GAP}px;
  padding: ${PADDING}px;
  background: var(--background);
  border: ${BORDER}px solid var(--border);
  border-radius: var(--radius, 8px);
  box-shadow: var(--elevation-toast);
`;

const Option = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${OPTION_SIZE}px;
  height: ${OPTION_SIZE}px;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm, 6px);
  background: none;
  font-size: 17px;
  line-height: 1;
  cursor: pointer;
  transition: background var(--transition-fast, 0.15s) ease;

  &:hover {
    background: var(--surface-elevated);
  }
`;
